import React, { useContext, useEffect, useMemo, useCallback, useState, useRef } from "react";
import { themeContext } from "../../context/theme";
import { gameContext } from "../../context/game";
import { useGamepad } from "../../hooks/useGamepad";
import { playSound } from "../../utilities/utilities";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import styles from "./ProfileModal.module.css";

export const ProfileModal = ({
  isVisible,
  user,
  onSignOut,
  onClose,
  game: gameProp,
  isSigningOut = false,
}) => {
  const { theme } = useContext(themeContext);
  const gameFromContext = useContext(gameContext);
  const game = gameProp || gameFromContext;
  const themeNumber = theme.replace("theme-", "");

  // Focusable elements for navigation
  const [gamepadEnabled, setGamepadEnabled] = useState(false);
  const modalRef = useRef(null);

  const focusableElements = useMemo(() => {
    return { elements: null, index: 0 };
  }, []);

  // Generate avatar initials from user name or email
  const getAvatarInitials = (user) => {
    if (user?.name && user.name.length > 0) {
      return user.name.split(' ')
        .map(name => name[0])
        .join('')
        .substring(0, 2)
        .toUpperCase();
    }
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  // Enable gamepad with delay to prevent immediate button press from opening/closing modal
  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => {
        setGamepadEnabled(true);
      }, 200);
      return () => clearTimeout(timer);
    } else {
      setGamepadEnabled(false);
    }
  }, [isVisible]);

  // Initialize focusable elements and auto-focus modal when opened
  useEffect(() => {
    if (isVisible && modalRef.current) {
      // Focus the modal container so keyboard events work
      modalRef.current.focus();

      if (gamepadEnabled) {
        const elements = modalRef.current.querySelectorAll(`.${styles.modalButton}`);
        focusableElements.elements = elements;
        focusableElements.index = 0;
      }
    }
  }, [isVisible, gamepadEnabled, focusableElements]);

  // Handle gamepad navigation
  const handleGamepadNavigation = useCallback((direction) => {
    if (!modalRef.current) return;

    const elements = modalRef.current.querySelectorAll(`.${styles.modalButton}`);
    if (elements.length === 0) return;

    focusableElements.elements = elements;

    if (focusableElements.index === undefined) {
      focusableElements.index = 0;
    }

    if (direction === 'left' || direction === 'up') {
      if (focusableElements.index <= 0) {
        focusableElements.index = elements.length - 1;
      } else {
        focusableElements.index--;
      }
    } else if (direction === 'right' || direction === 'down') {
      if (focusableElements.index >= elements.length - 1) {
        focusableElements.index = 0;
      } else {
        focusableElements.index++;
      }
    }

    if (elements[focusableElements.index]) {
      if (game) {
        playSound(game, "navigation");
      }
      elements[focusableElements.index].focus();
    }
  }, [focusableElements, game]);

  // Handle gamepad button presses
  const handleGamepadButtonPress = useCallback((button) => {
    if (button === 'A') {
      if (!modalRef.current) return;

      const elements = modalRef.current.querySelectorAll(`.${styles.modalButton}`);
      if (elements[focusableElements.index]) {
        elements[focusableElements.index].click();
        game?.sounds?.clickSound?.play();
      }
    } else if (button === 'B' || button === 'start') {
      // Close modal with B button or Start button
      onClose();
      game?.sounds?.clickSound?.play();
    }
  }, [focusableElements, onClose, game]);

  // Use the useGamepad hook
  useGamepad({
    onNavigate: handleGamepadNavigation,
    onButtonPress: handleGamepadButtonPress,
    navigationDelay: 150,
    buttonDelay: 150,
    enabled: gamepadEnabled,
    gamepadIndex: null
  });

  if (!isVisible) return null;

  return (
    <div
      className={styles.modalOverlay}
      onClick={(e) => {
        // Close modal if clicking on overlay (outside the modal box)
        if (e.target === e.currentTarget) {
          onClose();
          game?.sounds?.clickSound?.play();
        }
      }}
    >
      <div
        ref={modalRef}
        className={`${styles.modal} ${styles[`modal-theme-${themeNumber}`]}`}
        onKeyDown={(event) => {
          const modal = event.currentTarget;
          const elements = modal.querySelectorAll(`.${styles.modalButton}`);
          if (elements.length === 0) return;

          focusableElements.elements = elements;

          if (focusableElements.index === undefined) {
            focusableElements.index = 0;
          }

          if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (focusableElements.index <= 0) {
              focusableElements.index = elements.length - 1;
            } else {
              focusableElements.index--;
            }
            if (elements[focusableElements.index]) {
              if (game) {
                playSound(game, "navigation");
              }
              elements[focusableElements.index].focus();
            }
          } else if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (focusableElements.index >= elements.length - 1) {
              focusableElements.index = 0;
            } else {
              focusableElements.index++;
            }
            if (elements[focusableElements.index]) {
              if (game) {
                playSound(game, "navigation");
              }
              elements[focusableElements.index].focus();
            }
          } else if (event.key === 'Enter') {
            event.preventDefault();
            if (elements[focusableElements.index]) {
              elements[focusableElements.index].click();
            }
          } else if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            onClose();
            game?.sounds?.clickSound?.play();
          }
        }}
        onMouseMove={(event) => {
          const modal = event.currentTarget;
          const elements = modal.querySelectorAll(`.${styles.modalButton}`);
          if (elements.length > 0) {
            focusableElements.elements = elements;
            // Mouse mode - blur focused elements on hover
            elements.forEach((element, index) => {
              if (event.target === element && index !== focusableElements.index) {
                elements.forEach(el => {
                  if (el === document.activeElement) {
                    el.blur();
                  }
                });
                focusableElements.index = index;
              }
            });
          }
        }}
        tabIndex={0}
      >
        {/* Close button */}
        <button
          className={`${styles.closeButton} ${styles[`closeButton-theme-${themeNumber}`]}`}
          onClick={onClose}
          tabIndex="-1"
          aria-label="Close"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <div className={styles.modalContent}>
          {/* Profile Header */}
          <div className={styles.profileHeader}>
            <div className={`${styles.profileAvatar} ${styles[`profileAvatar-theme-${themeNumber}`]}`}>
              {getAvatarInitials(user)}
            </div>
            <div className={styles.profileInfo}>
              <div className={styles.profileName}>
                {user?.name || user?.email?.split('@')[0]}
              </div>
              <div className={styles.profileEmail}>
                {user?.email}
              </div>
            </div>
          </div>

          {/* Sign Out Button */}
          <div className={styles.actionsSection}>
            <button
              onClick={onSignOut}
              disabled={isSigningOut}
              className={`${styles.modalButton} ${styles.signOutButton} ${styles[`signOutButton-theme-${themeNumber}`]}`}
            >
              {isSigningOut ? 'Signing Out...' : 'Sign Out'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
